import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useLeadQuery } from '../leads/api.js';
import { useActivitiesQuery } from '../activities/api.js';
import { useTasksQuery } from '../tasks/api.js';
import { MobileBottomNav, avatarColor, initialsOf, statusPillStyle } from './MobileNav.js';
import { QuickLogSheet } from './QuickLogSheet.js';

type SectionTab = 'timeline' | 'details' | 'tasks';

const KIND_STYLE: Record<string, { bg: string; fg: string; label: string; path: string }> = {
  call: { bg: '#E6F4EC', fg: '#1E7A4C', label: 'Call', path: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2' },
  email: { bg: '#FEF3E2', fg: '#8A4B06', label: 'Email', path: 'M3 5h18v14H3zM3 7l9 6 9-6' },
  meeting: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Meeting', path: 'M3 10h18M16 3v4M8 3v4M3 5h18v16H3z' },
  note: { bg: '#FEF3E2', fg: '#8A4B06', label: 'Note', path: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 13h8M8 17h5' },
  stage_changed: { bg: '#F1ECFD', fg: '#5B34B0', label: 'Status changed', path: 'M5 12h14M13 6l6 6-6 6' },
  assigned: { bg: '#E3F5F3', fg: '#0F6B63', label: 'Assigned', path: 'M12 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8' },
  system: { bg: '#ECEFFD', fg: '#2E47D6', label: 'System', path: 'M12 3v18M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' },
  created: { bg: '#ECEFFD', fg: '#2E47D6', label: 'Lead created', path: 'M12 3v18M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' },
  task_completed: { bg: '#E6F4EC', fg: '#1E7A4C', label: 'Task completed', path: 'm5 12 5 5 9-10' },
};

function formatFollowUp(iso: string | null): string {
  if (!iso) return 'No follow-up set';
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

export function MobileLeadDetailPage() {
  const { leadId } = useParams({ from: '/m/leads/$leadId' });
  const navigate = useNavigate();
  const [tab, setTab] = useState<SectionTab>('timeline');
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const { data: lead, isLoading } = useLeadQuery(leadId);
  const { data: activities } = useActivitiesQuery({ leadId });
  const { data: tasks } = useTasksQuery({ leadId, view: 'mine' });

  if (isLoading || !lead) {
    return (
      <div className="flex h-dvh flex-col bg-ground text-ink">
        <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-1.5">
          <Link to="/m/leads" aria-label="Back to leads" className="flex size-11 items-center justify-center rounded-control">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m15 6-6 6 6 6" />
            </svg>
          </Link>
          <h1 className="m-0 flex-1 text-center text-[16px] font-semibold">Lead</h1>
          <span className="size-11" />
        </header>
      </div>
    );
  }

  const pill = statusPillStyle(lead.status.color);
  const location = [lead.address?.city, lead.address?.state].filter(Boolean).join(', ');

  return (
    <div className="flex h-dvh flex-col bg-ground text-ink">
      <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-1.5">
        <Link to="/m/leads" aria-label="Back to leads" className="flex size-11 items-center justify-center rounded-control">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m15 6-6 6 6 6" />
          </svg>
        </Link>
        <h1 className="m-0 flex-1 text-center text-[16px] font-semibold">Lead</h1>
        <button type="button" aria-label="Edit lead" onClick={() => navigate({ to: '/leads/$leadId', params: { leadId } })} className="flex size-11 items-center justify-center rounded-control">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 20h4L19 9l-4-4L4 16z" />
            <path d="m13 7 4 4" />
          </svg>
        </button>
        <button type="button" aria-label="More actions" onClick={() => navigate({ to: '/leads/$leadId', params: { leadId } })} className="flex size-11 items-center justify-center rounded-control">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true">
            <circle cx="5" cy="12" r="0.8" fill="currentColor" />
            <circle cx="12" cy="12" r="0.8" fill="currentColor" />
            <circle cx="19" cy="12" r="0.8" fill="currentColor" />
          </svg>
        </button>
      </header>

      <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-2 pt-3">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full text-base font-[650] text-white" style={{ background: avatarColor(lead.fullName) }}>
              {initialsOf(lead.fullName)}
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">{lead.fullName}</span>
              <span className="text-sm text-text-secondary">
                {lead.companyName ?? '—'}
                {location ? ` · ${location}` : ''}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex h-6 items-center gap-1.5 rounded-pill px-2.5 text-xs font-semibold" style={{ background: pill.bg, color: pill.fg }}>
              <span className="size-1.5 rounded-full" style={{ background: pill.fg }} />
              {lead.status.name}
            </span>
            {lead.temperature === 'hot' && (
              <span className="flex h-5.5 items-center gap-1 rounded-control bg-warning-soft px-2 text-xs font-semibold text-warning-text">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C7781A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s1 2 2 2c0-3-1-5 1-8" />
                </svg>
                Hot
              </span>
            )}
            <span className="flex-1" />
            {lead.score !== null && (
              <span aria-label={`Lead score ${lead.score} of 100`} className="flex items-center gap-2 text-[13px] font-semibold">
                <span className="text-xs font-medium text-text-muted">Score</span>
                <span className="flex h-1.5 w-12 rounded-pill bg-divider">
                  <span className="rounded-pill bg-success" style={{ width: `${lead.score}%` }} />
                </span>
                {lead.score}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <a href={lead.phone ? `tel:${lead.phone}` : undefined} aria-disabled={!lead.phone} className={`flex flex-col items-center gap-1.5 text-xs font-semibold text-text-secondary no-underline ${!lead.phone ? 'pointer-events-none opacity-40' : ''}`}>
            <span className="flex size-12 items-center justify-center rounded-full border border-border bg-surface shadow-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-primary">
                <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
              </svg>
            </span>
            Call
          </a>
          <a href={lead.email ? `mailto:${lead.email}` : undefined} aria-disabled={!lead.email} className={`flex flex-col items-center gap-1.5 text-xs font-semibold text-text-secondary no-underline ${!lead.email ? 'pointer-events-none opacity-40' : ''}`}>
            <span className="flex size-12 items-center justify-center rounded-full border border-border bg-surface shadow-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-primary">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
            </span>
            Email
          </a>
          <button type="button" onClick={() => setShowQuickAdd(true)} className="flex flex-col items-center gap-1.5 text-xs font-semibold text-text-secondary">
            <span className="flex size-12 items-center justify-center rounded-full border border-border bg-surface shadow-card">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-primary">
                <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <path d="M14 3v6h6M8 13h8M8 17h5" />
              </svg>
            </span>
            Log
          </button>
          <button type="button" onClick={() => navigate({ to: '/leads/$leadId', params: { leadId } })} className="flex flex-col items-center gap-1.5 text-xs font-semibold text-primary">
            <span className="flex size-12 items-center justify-center rounded-full bg-primary shadow-[0_4px_12px_rgba(46,71,214,0.24)]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 17 17 7" />
                <path d="M8 7h9v9" />
              </svg>
            </span>
            Convert
          </button>
        </div>

        <div role="tablist" aria-label="Lead sections" className="grid h-10 shrink-0 grid-cols-3 gap-1 rounded-input bg-surface-subtle p-0.75">
          {(
            [
              { key: 'timeline' as const, label: 'Timeline' },
              { key: 'details' as const, label: 'Details' },
              { key: 'tasks' as const, label: `Tasks${tasks?.meta?.total ? ` ${tasks.meta.total}` : ''}` },
            ]
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center justify-center rounded-chip text-sm ${tab === t.key ? 'bg-surface font-semibold text-ink shadow-card ring-1 ring-border' : 'font-medium text-text-secondary'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'timeline' && (
          <ol className="m-0 flex flex-col" style={{ listStyle: 'none' }}>
            {(activities?.data ?? []).map((a, i, arr) => {
              const style = KIND_STYLE[a.kind] ?? { bg: '#EFEEEA', fg: '#5E6470', label: a.kind, path: 'M12 8v4l3 2' };
              const isLast = i === arr.length - 1;
              return (
                <li key={a.id} className="flex gap-3">
                  <span className="flex w-8 shrink-0 flex-col items-center">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full" style={{ background: style.bg }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={style.fg} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d={style.path} />
                      </svg>
                    </span>
                    {!isLast && <span className="my-1 w-0.5 flex-1 bg-border" />}
                  </span>
                  <span className={`flex flex-1 flex-col gap-0.5 ${isLast ? 'pb-0 pt-1.5' : 'pb-3.5 pt-1.5'}`}>
                    {a.kind === 'system' && a.metadata?.event === 'lead_status_changed' ? (
                      <span className="flex items-center gap-1.5 text-sm font-semibold">
                        Status
                        <span className="flex h-5 items-center rounded-pill bg-surface-subtle px-2 text-[11px] font-semibold text-text-secondary">{String(a.metadata.fromStatusName ?? '—')}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-secondary">
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                        <span className="flex h-5 items-center rounded-pill bg-primary-soft px-2 text-[11px] font-semibold text-primary">{String(a.metadata.toStatusName ?? '—')}</span>
                      </span>
                    ) : a.kind === 'assigned' && a.metadata?.toOwnerName ? (
                      <span className="text-sm font-semibold">Assigned to {String(a.metadata.toOwnerName)}</span>
                    ) : a.kind === 'created' ? (
                      <span className="text-sm font-semibold">Lead created from {lead.source?.name ?? 'an unknown source'}</span>
                    ) : (
                      <span className="text-sm font-semibold">
                        {style.label} {a.outcome && <span className="font-medium text-success">· {a.outcome}</span>}
                      </span>
                    )}
                    {a.bodyText && <span className="text-[13px] leading-4.5 text-text-secondary">{a.bodyText}</span>}
                    <span className="text-xs text-text-muted">
                      {new Date(a.occurredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {a.actor?.displayName ?? 'System'}
                    </span>
                  </span>
                </li>
              );
            })}
            {(activities?.data.length ?? 0) === 0 && <p className="text-sm text-text-muted">No activity yet.</p>}
          </ol>
        )}

        {tab === 'details' && (
          <dl className="m-0 flex flex-col gap-3 text-sm">
            {[
              ['Email', lead.email],
              ['Phone', lead.phone],
              ['Job title', lead.jobTitle],
              ['Website', lead.website],
              ['Source', lead.source?.name],
              ['Owner', lead.owner?.displayName ?? 'Unassigned'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 border-b border-divider pb-2">
                <dt className="text-text-muted">{label}</dt>
                <dd className="m-0 truncate text-right font-medium">{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
        )}

        {tab === 'tasks' && (
          <ul className="m-0 flex flex-col gap-2" style={{ listStyle: 'none' }}>
            {(tasks?.data ?? []).map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 rounded-control border border-border bg-surface p-3 text-sm shadow-card">
                <span className="font-medium">{t.title}</span>
                <span className="text-xs text-text-muted">{t.dueAt ? new Date(t.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</span>
              </li>
            ))}
            {(tasks?.data.length ?? 0) === 0 && <p className="text-sm text-text-muted">No tasks yet.</p>}
          </ul>
        )}
      </main>

      <div className="flex h-18 shrink-0 items-center gap-3 border-t border-border bg-surface px-4 shadow-[0_-4px_12px_rgba(22,24,29,0.04)]">
        <span className="flex flex-1 flex-col gap-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Next follow-up</span>
          <span className={`flex items-center gap-1.5 text-sm font-semibold ${lead.nextFollowUpAt ? 'text-danger' : 'text-text-muted'}`}>
            {lead.nextFollowUpAt && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            )}
            {formatFollowUp(lead.nextFollowUpAt)}
          </span>
        </span>
        <button type="button" onClick={() => setShowQuickAdd(true)} className="flex h-11 items-center gap-2 rounded-input bg-primary px-5 text-[15px] font-semibold text-white">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Log activity
        </button>
      </div>

      <MobileBottomNav active="leads" />
      {showQuickAdd && <QuickLogSheet leadId={leadId} onClose={() => setShowQuickAdd(false)} />}
    </div>
  );
}
